import React, {useEffect, useState} from 'react'
import {Button} from 'react-bootstrap'
import axios from 'axios'
import Select from 'react-select'
import {CommonLoading} from '../../../../Utils/commonLoading'
import toaster from '../../../../Utils/toaster'

const API_URL = process.env.REACT_APP_API_URL
const ALL_LANGUAGE_VALUE = 'alllanguage'

type DirectDialEntry = { language: string | null; ivrNumber: string | null }

type IvrOption = { value: string; label: string; assignee?: string | null }

type DirectDialViewProps = {
  userId: string
  customerCode?: string | number
}

export function DirectDialView({userId, customerCode}: DirectDialViewProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [languages, setLanguages] = useState<{key: string; value: string}[]>([])
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false)
  const [isLoadingIvr, setIsLoadingIvr] = useState(false)
  const [ivrOptions, setIvrOptions] = useState<IvrOption[]>([])
  const [hasDirectDial, setHasDirectDial] = useState(false)
  const [allLanguages, setAllLanguages] = useState(false)
  const [entries, setEntries] = useState<DirectDialEntry[]>([{language: null, ivrNumber: null}])

  const fetchCustomerLanguages = async (customerCodeParam: string | number | undefined) => {
    if (!customerCodeParam) return
    setIsLoadingLanguages(true)
    try {
      const response = await axios.get(`${API_URL}/customer/${customerCodeParam}/languages`)
      const responseData = response?.data?.result || []
      const transformed = Array.isArray(responseData)
        ? responseData.map((item: any) => ({
            key: item.code,
            value: item.name,
          }))
        : []

      const languagesWithOperator = [...transformed, {key: 'OPI', value: 'Operator'}]
      setLanguages(languagesWithOperator)
    } catch (error) {
      console.error(error)
      setLanguages([])
    } finally {
      setIsLoadingLanguages(false)
    }
  }

  const fetchIvrNumbers = async () => {
    setIsLoadingIvr(true)
    try {
      const response = await axios.get(`${API_URL}/master/ivr-numbers`, {
        params: {page: 1, items_per_page: 1000},
      })
      const data = response?.data?.data || []
      const options = data
        .filter(
          (d: {isDeleted: boolean; isGlobal?: boolean; IsGlobal?: boolean}) =>
            !d.isDeleted && !(d.isGlobal ?? d.IsGlobal)
        )
        .map(
          (d: {
            value: string
            code?: string
            Code?: string
            assignee?: string | null
            Assignee?: string | null
          }) => {
            const code = d.code ?? d.Code ?? d.value
            return {
              value: d.value,
              assignee: d.assignee ?? d.Assignee ?? null,
              label: `${code} - ${d.value}`,
            }
          }
        )
      setIvrOptions(options)
    } catch (error) {
      console.error(error)
      setIvrOptions([])
    } finally {
      setIsLoadingIvr(false)
    }
  }

  const getExistingDirectDial = async (requesterId: string | undefined) => {
    setIsLoading(true)
    if (!requesterId) return
    try {
      const response = await axios.get(
        `${API_URL}/customerdirectdial/GetByRequesterId/${requesterId}`
      )
      setHasDirectDial(response?.data?.hasDirectDial ?? false)
      setAllLanguages(response?.data?.allLanguages ?? false)
      const list = response?.data?.entries
      if (Array.isArray(list) && list.length > 0) {
        setEntries(
          list.map((e: { language?: string | null; ivrNumber?: string | null }) => ({
            language: e.language ?? ALL_LANGUAGE_VALUE,
            ivrNumber: e.ivrNumber ?? null,
          }))
        )
      } else {
        setEntries([{language: null, ivrNumber: null}])
      }
    } catch (error) {
      console.error(error)
      toaster('error', 'Error fetching Direct Dial')
    } finally {
      setIsLoading(false)
    }
  }

  const updateDirectDial = async (
    requesterId: string | undefined,
    hasDirect: boolean,
    allLangs: boolean,
    entryList: DirectDialEntry[]
  ) => {
    setIsLoading(true)
    if (!requesterId) return
    const payloadEntries = entryList
      .filter((e) => e.ivrNumber != null && e.ivrNumber.trim() !== '')
      .map((e) => ({language: e.language ?? undefined, ivrNumber: e.ivrNumber!.trim()}))
    try {
      const res = await axios.put(`${API_URL}/customerdirectdial/UpdateByRequesterId`, {
        requesterId,
        hasDirectDial: hasDirect,
        allLanguages: allLangs,
        entries: payloadEntries,
      })
      if (res?.data?.status === 'E') {
        toaster('error', res?.data?.text ?? 'Unable to update Direct Dial')
        return
      }
      toaster('success', 'Direct Dial updated successfully')
      await Promise.all([getExistingDirectDial(requesterId), fetchIvrNumbers()])
    } catch (error: any) {
      const msg = error?.response?.data?.text ?? 'Unable to update Direct Dial'
      toaster('error', msg)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (userId) {
      getExistingDirectDial(userId)
    }
  }, [userId])

  useEffect(() => {
    if (customerCode !== undefined && customerCode !== null && customerCode !== '') {
      fetchCustomerLanguages(customerCode)
    }
  }, [customerCode])

  useEffect(() => {
    fetchIvrNumbers()
  }, [])

  const isEntryComplete = (entry: DirectDialEntry) =>
    !!entry.language?.trim() && !!entry.ivrNumber?.trim()

  const isEntryEmpty = (entry: DirectDialEntry) =>
    !entry.language?.trim() && !entry.ivrNumber?.trim()

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const hasPartial = entries.some((entry) => !isEntryComplete(entry) && !isEntryEmpty(entry))
    if (hasPartial) {
      toaster(
        'error',
        'Please select both Language and IVR Number for every row. Empty values are not allowed.'
      )
      return
    }

    const completeEntries = entries.filter(isEntryComplete)
    if (completeEntries.length === 0) {
      updateDirectDial(userId, false, false, [])
      return
    }

    const ivrValues = completeEntries.map((entry) => entry.ivrNumber!)
    const hasDuplicates = ivrValues.length !== new Set(ivrValues).size
    if (hasDuplicates) {
      toaster('error', 'Duplicate IVR numbers are not allowed for the same requester.')
      return
    }
    updateDirectDial(userId, hasDirectDial, allLanguages, completeEntries)
  }

  const addEntryRow = () => {
    setEntries((prev) => [...prev, {language: null, ivrNumber: null}])
  }

  const setEntryAt = (index: number, field: 'language' | 'ivrNumber', value: string | null) => {
    setEntries((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const removeEntryAt = (index: number) => {
    setEntries((prev) => {
      const next = prev.filter((_, i) => i !== index)
      if (next.length === 0) {
        setHasDirectDial(false)
        setAllLanguages(false)
      }
      return next
    })
  }

  const getIvrOptionsForIndex = (index: number) => {
    const currentValue = entries[index]?.ivrNumber
    const requesterIvrNumbers = new Set(
      entries.map((e) => e.ivrNumber).filter((v): v is string => !!v)
    )
    const selectedElsewhere = new Set(
      entries.map((e, i) => (i !== index && e.ivrNumber ? e.ivrNumber : null)).filter(Boolean)
    )
    return ivrOptions.filter((opt) => {
      const isAssignedToOther =
        !!opt.assignee &&
        opt.value !== currentValue &&
        !requesterIvrNumbers.has(opt.value)
      if (isAssignedToOther) return false
      return opt.value === currentValue || !selectedElsewhere.has(opt.value)
    })
  }

  const normLang = (key: string) =>
    key.toUpperCase() === 'OPI' ? 'OPI' : key.toLowerCase()

  return (
    <>
      <div className='card-body p-0'>
        <div className='card-title d-flex align-items-center me-4 mb-3'>
          <div className='d-flex flex-column'>
            <div className='d-flex align-items-center'>
              <h5 className='text-black fs-4 fw-semibold mb-0'>Direct Dial</h5>
            </div>
            <div className='d-flex flex-wrap fs-6'>
              <p className='text-gray-500 mb-0 mt-4 small w-100'>
                Configure quick access language and IVR numbers for this user. You can assign
                multiple IVR numbers; each must be different. Only languages eligible for the
                user&apos;s customer are available.
              </p>
            </div>
          </div>
        </div>
        {isLoading || isLoadingLanguages || isLoadingIvr ? (
          <CommonLoading />
        ) : (
          <React.Fragment>
            <div>
              <div className='form-check form-switch d-flex align-items-center mt-5'>
                <input
                  className='form-check-input w-45px h-30px cursor-pointer'
                  type='checkbox'
                  id='hasDirectDial'
                  checked={hasDirectDial}
                  onChange={() =>
                    setHasDirectDial((prev) => {
                      const next = !prev
                      if (!next) setAllLanguages(false)
                      return next
                    })
                  }
                />
                <label
                  className='form-check-label ml-4 d-inline-block ms-3 text-muted fs-5 cursor-pointer'
                  htmlFor='hasDirectDial'
                >
                  Enable Direct Dial
                </label>
              </div>
              {/* <div className='form-check form-switch d-flex align-items-center mt-4'>
                <input
                  className='form-check-input w-45px h-30px cursor-pointer'
                  type='checkbox'
                  id='allLanguages'
                  checked={allLanguages}
                  disabled={!hasDirectDial}
                  onChange={() => setAllLanguages(!allLanguages)}
                />
                <label
                  className='form-check-label ml-4 d-inline-block ms-3 text-muted fs-5 cursor-pointer'
                  htmlFor='allLanguages'
                >
                  All Languages
                </label>
              </div> */}
            </div>

            <div className='mt-5'>
              <form onSubmit={handleFormSubmit}>
                {entries.map((entry, index) => (
                  <div key={index} className='row align-items-end mb-3'>
                    <div className='col-md-5'>
                      <label className='form-label fw-bold text-black fs-6'>Language</label>
                      <select
                        className='form-select form-select-solid form-select-md w-100'
                        value={entry.language ?? ''}
                        onChange={(e) => {
                          const v = e.target.value
                          setEntryAt(index, 'language', !v ? null : normLang(v))
                        }}
                      >
                        <option value=''>Select Language</option>
                        <option value={ALL_LANGUAGE_VALUE}>All Languages</option>
                        {languages.map((lang) => (
                          <option key={lang.key} value={normLang(lang.key)}>
                            {lang.value}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-md-5'>
                      <label className='form-label fw-bold text-black fs-6'>IVR Number</label>
                      <Select
                        placeholder='Select an IVR Number'
                        value={
                          getIvrOptionsForIndex(index).find(
                            (opt) => opt.value === (entry.ivrNumber ?? '')
                          ) || null
                        }
                        onChange={(option: {value: string} | null) =>
                          setEntryAt(index, 'ivrNumber', option?.value ?? null)
                        }
                        options={getIvrOptionsForIndex(index)}
                        styles={{
                          control: (provided: any) => ({
                            ...provided,
                            minHeight: '38px',
                          }),
                        }}
                      />
                    </div>
                    <div className='col-md-2 d-flex align-items-end pb-2'>
                      <button
                        type='button'
                        className='btn btn-icon btn-light-danger btn-sm'
                        onClick={() => removeEntryAt(index)}
                        title='Remove'
                      >
                        <i className='bi bi-trash fs-5' />
                      </button>
                    </div>
                  </div>
                ))}

                <div className='row mb-3'>
                  <div className='col-md-12'>
                    <button
                      type='button'
                      className='btn btn-light-primary btn-sm d-inline-flex align-items-center'
                      onClick={addEntryRow}
                    >
                      <i className='bi bi-plus fs-2 me-2' />
                      Add Language & IVR Number
                    </button>
                  </div>
                </div>

                <div className='d-flex justify-content-end mt-3'>
                  <Button variant='primary' type='submit'>
                    Update
                  </Button>
                </div>
              </form>
            </div>
          </React.Fragment>
        )}
      </div>
    </>
  )
}
